import React from 'react';
import { RatingDesktop, RatingMobile, RatingOption } from './Rating';
import _ from 'lodash';
import UUID from '@ax2/common/model/UUID';
import styles from '../../styles/components/answers/RatingGrid.module.css';

export interface RatingGridColOption {
  id: UUID;
  title?: string;
  subtitle?: string;
}
export interface RatingGridRowOption {
  id: UUID;
  title?: string;
}
export interface RatingGridOption {
  col: RatingGridColOption;
  row: RatingGridRowOption;
  selected: boolean;
}
export interface RatingGridData {
  cols: RatingGridColOption[];
  rows: RatingGridRowOption[];
  isDesktop: boolean;
  preselected?: RatingGridOption[];
  onCompletion: (opts: RatingGridOption[]) => void;
  key?: string;
}

function getInitialState(data: RatingGridData): RatingGridOption[][] {
  const state = _.map(data.rows, row => {
    return _.map(data.cols, col => {
      const preOpt = _.find(
        data.preselected,
        p => p.col.id === col.id && p.row.id === row.id
      );
      const opt: RatingGridOption = preOpt
        ? preOpt
        : {
            col: col,
            row: row,
            selected: false,
          };
      return opt;
    });
  });
  return state;
}

function getRatingSelection(
  data: RatingGridOption[]
): RatingOption | undefined {
  const selection = _.find(data, d => d.selected);
  return selection ? selection.col : undefined;
}

function getNormalizedData(data: RatingGridOption[][]): RatingGridOption[] {
  return _.filter(_.flatten(data), d => !!d.selected);
}

export const RatingGrid: React.FC<RatingGridData> = data => {
  const [selectionData, setSelectionData] = React.useState<
    RatingGridOption[][]
  >(getInitialState(data));

  const getRatingOptions = (rowData: RatingGridOption[], index: number) => ({
    options: _.map(rowData, d => d.col),
    preselected: getRatingSelection(rowData),
    tabIndexStart: index ? rowData.length * index : 0,
    onCompletion: (s: RatingOption) => {
      const newSelectionData = [...selectionData];
      newSelectionData[index] = _.map(rowData, d => {
        if (d.col.id === s.id) {
          d.selected = true;
        } else {
          d.selected = false;
        }
        return d;
      });
      setSelectionData(newSelectionData);
      data.onCompletion(getNormalizedData(newSelectionData));
    },
  });

  return (
    <div className={styles.ratingContainer}>
      {_.map(selectionData, (rowData, index) => (
        <div
          key={`rating-cell-${index}`}
          className={
            data.isDesktop
              ? styles.ratingCellWrapper
              : styles.ratingCellMobileWrapper
          }
        >
          <span className={styles.ratingCellText}>
            {rowData[0] ? rowData[0].row.title : ''}
          </span>
          {data.isDesktop ? (
            <RatingDesktop {...getRatingOptions(rowData, index)} />
          ) : (
            <RatingMobile {...getRatingOptions(rowData, index)} />
          )}
        </div>
      ))}
    </div>
  );
};
